import React, { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { sortBy } from 'lodash-es'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import RefreshButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/refreshButton/RefreshButton'
import ChevronIcon from '@lyrahealth-inc/ui-core/lib/atoms/icons/ChevronIcon'
import Dashlet from '@lyrahealth-inc/ui-core/lib/molecules/layouts/dashlet/Dashlet'

import styles from './treatmentSummaryLetterDashlet.module.scss'
import { getClientFullName } from '../../../common/utils/utils'
import { useGetProviderTslTasksQuery } from '../../../providers/dashboard/data/providerTasksApi'
import { useAllClientsData } from '../../clients/data/hooks/useAllClientsData'
import { TslReviewModal } from '../../treatmentSummaryLetter/components/TslReviewModal'
import { toTslTaskRow } from '../../treatmentSummaryLetter/utils/tslTaskUtils'

type TreatmentSummaryLetterDashletProps = {
  providerId: string
}

export const TreatmentSummaryLetterDashlet: React.FC<TreatmentSummaryLetterDashletProps> = ({ providerId }) => {
  const intl = useIntl()
  const { isPreferredNameEnabled, isTSLEnabled } = useFlags()
  const [selectedLetterId, setSelectedLetterId] = useState<string | null>(null)
  const [clients, activeClientsQuery, inactiveClientsQuery] = useAllClientsData(undefined, { skip: !isTSLEnabled })
  const {
    data: treatmentSummaryLetterTasks,
    isLoading: isLoadingTasks,
    isFetching: isRefreshingTasks,
    refetch: refetchTasks,
  } = useGetProviderTslTasksQuery({ providerId }, { skip: !isTSLEnabled })

  const tasks = useMemo(
    () =>
      sortBy(
        (treatmentSummaryLetterTasks ?? []).map((task) => {
          const row = toTslTaskRow(task, intl)
          const client = clients.find(({ id }) => id === task.arguments?.patient_lyra_id)
          return client ? { ...row, clientName: getClientFullName(client, isPreferredNameEnabled) } : row
        }),
        ({ date }) => date ?? '',
      ),
    [clients, intl, isPreferredNameEnabled, treatmentSummaryLetterTasks],
  )

  const isLoading = isLoadingTasks || activeClientsQuery.isLoading || inactiveClientsQuery.isLoading
  const title = intl.formatMessage({
    defaultMessage: 'Treatment summary letter requests',
    description: 'Legacy provider dashboard TSL dashlet title',
  })

  if (!isTSLEnabled) return null

  return (
    <>
      <div className={styles['dashlet-container']} data-test-id='TreatmentSummaryLetterDashlet-content'>
        <Dashlet
          title={`${title}${isLoading ? '' : ` (${tasks.length})`}`}
          buttons={<RefreshButton size='20' isLoading={isRefreshingTasks} onClick={refetchTasks} />}
          isLoading={isLoading}
        >
          {tasks.length === 0 ? (
            <div className={styles['empty-dashlet']}>
              <FormattedMessage
                defaultMessage='No treatment summary letters to review'
                description='Empty legacy provider dashboard TSL dashlet'
              />
            </div>
          ) : (
            tasks.map((task) => (
              <div className={styles['dashlet-item-container']} key={task.taskId}>
                <button
                  type='button'
                  className={styles['dashlet-item-content']}
                  data-test-id={`TreatmentSummaryLetterDashlet-list-item-${task.taskId}`}
                  onClick={() => setSelectedLetterId(task.letterId)}
                >
                  <div className={styles.left}>
                    <div className={styles.name}>{task.clientName}</div>
                    <div className={styles.subtext}>{task.dateRange}</div>
                  </div>
                  <div className={styles.right}>
                    {task.date && (
                      <div className={styles.date}>
                        {intl.formatDate(task.date, { year: 'numeric', month: '2-digit', day: '2-digit' })}
                      </div>
                    )}
                    <ChevronIcon isFilled fillColor={styles.x_soft_black} direction='right' />
                  </div>
                </button>
              </div>
            ))
          )}
        </Dashlet>
      </div>
      <TslReviewModal letterId={selectedLetterId} onClose={() => setSelectedLetterId(null)} />
    </>
  )
}

export default TreatmentSummaryLetterDashlet
